import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Kommonview - A Technology Company',
  description = 'Kommonview is a cloud-first technology company that designs, builds, and operates modern digital infrastructure at scale. We deliver multi-cloud excellence (AWS, Azure, GCP), secure enterprise platforms, Atlassian-powered collaboration, bespoke software solutions, and high-impact digital advertising.',
  keywords = 'cloud infrastructure, AWS, Azure, GCP, multi-cloud solutions, DevOps, Atlassian, Jira, Confluence, IT infrastructure, digital transformation, enterprise platforms, software development, cloud migration, South Africa technology company',
  image = 'https://kommonview.com/images/og-image.jpg',
  url = 'https://kommonview.com',
  type = 'website',
  author = 'Kommonview',
}) => {
  const siteTitle = title.includes('Kommonview') ? title : `${title} | Kommonview`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="title" content={siteTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Kommonview" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:creator" content="@KommonView" />
      <meta name="twitter:site" content="@KommonView" />

      {/* Additional Meta Tags */}
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Kommonview',
          description: 'A Technology Company specializing in cloud infrastructure, DevOps, and enterprise platforms',
          url: 'https://kommonview.com',
          logo: 'https://kommonview.com/images/kommonview-logo.png',
          sameAs: [
            'https://www.linkedin.com/company/kommonview/',
            'https://x.com/KommonView/',
            'https://www.instagram.com/kommonview_',
            'https://www.facebook.com/kommonview/',
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+27',
            contactType: 'Customer Service',
            email: 'info@kommonview.com',
            availableLanguage: ['English'],
          },
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'ZA',
          },
          offers: {
            '@type': 'AggregateOffer',
            availability: 'https://schema.org/InStock',
            itemOffered: [
              {
                '@type': 'Service',
                name: 'Cloud Infrastructure Management',
                description: 'Multi-cloud solutions including AWS, Azure, and GCP',
              },
              {
                '@type': 'Service',
                name: 'DevOps Implementation',
                description: 'CI/CD pipelines and automation solutions',
              },
              {
                '@type': 'Service',
                name: 'Atlassian Cloud Services',
                description: 'Jira, Confluence, and Opsgenie implementation and support',
              },
              {
                '@type': 'Service',
                name: 'IT Infrastructure Services',
                description: 'Hardware, software, and networking solutions',
              },
            ],
          },
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
